import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { fetchMessages } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import { formatTime } from '../../hooks/useTimeAgo'
import { useAwakePersonas, isAwake as checkAwake } from '../../hooks/useAwakePersonas'

const JITSI_DOMAIN = 'meet.thegathering.space'

function generateRoomName(id?: string) {
  return id || `gathering-${Date.now().toString(36)}`
}

export default function VideoCallPage() {
  const { room } = useParams<{ room: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const awakeSet = useAwakePersonas()
  const jitsiRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const [roomName] = useState(() => generateRoomName(room))
  const [draft, setDraft] = useState('')
  const [convId, setConvId] = useState<string | null>(null)

  const displayName = user?.userName || user?.loginName || 'Guest'
  const senderId = user?.userId || user?.loginName || 'joel'

  // Create or find conversation for this video room
  useEffect(() => {
    async function ensureConversation() {
      const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`

      const res = await fetch('/api/conversations', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: `Video Call — ${roomName}`,
          type: 'group',
          participants: [senderId],
          visibility: 'open',
          room_type: 'workspace',
        }),
      })
      if (res.ok) {
        const data = await res.json()
        setConvId(data.id)
      }
    }
    ensureConversation()
  }, [roomName, senderId])

  // Load Jitsi Meet
  useEffect(() => {
    if (!jitsiRef.current) return

    const script = document.createElement('script')
    script.src = 'https://meet.thegathering.space/external_api.js'
    script.async = true
    script.onload = () => {
      if (!(window as any).JitsiMeetExternalAPI) return
      const api = new (window as any).JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName: `gathering-${roomName}`,
        parentNode: jitsiRef.current,
        width: '100%',
        height: '100%',
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          TOOLBAR_BUTTONS: [
            'microphone', 'camera', 'desktop', 'chat',
            'raisehand', 'tileview', 'hangup',
          ],
        },
        userInfo: {
          displayName: displayName,
        },
      })

      return () => api.dispose()
    }
    document.head.appendChild(script)

    return () => {
      script.remove()
    }
  }, [roomName, displayName])

  // Fetch messages for the linked conversation
  const { data: msgData } = useQuery({
    queryKey: ['video-call-messages', convId],
    queryFn: () => fetchMessages(convId!),
    enabled: !!convId,
    refetchInterval: 5000,
  })

  const messages = msgData?.messages || []

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  // Send message
  async function handleSend() {
    if (!draft.trim() || !convId) return
    const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`

    await fetch(`/api/conversations/${convId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ content: draft.trim(), sender_id: senderId }),
    })
    setDraft('')
    queryClient.invalidateQueries({ queryKey: ['video-call-messages', convId] })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const shareUrl = `${window.location.origin}/video/${roomName}`

  return (
    <div className="flex h-full">
      {/* Left: Video */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div
          className="shrink-0 flex items-center gap-3 px-4 py-2"
          style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}
        >
          <button
            onClick={() => navigate('/tech')}
            className="text-xs"
            style={{ color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ← Tech
          </button>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Video Call
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399' }}>
            Live
          </span>
          <div className="flex-1" />
          <button
            onClick={() => navigator.clipboard.writeText(shareUrl)}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}
          >
            Copy Invite Link
          </button>
        </div>

        {/* Jitsi Embed */}
        <div ref={jitsiRef} className="flex-1" style={{ background: '#000' }} />
      </div>

      {/* Right: Persona Chat */}
      <div
        className="w-80 shrink-0 flex flex-col"
        style={{ borderLeft: '1px solid var(--border)', background: 'var(--bg-surface)' }}
      >
        {/* Chat header */}
        <div
          className="shrink-0 px-4 py-3"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Persona Chat
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Personas participate here while you're on video
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {messages.length === 0 ? (
            <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>
              Personas will appear here during the call. They can see your messages and contribute to the conversation.
            </div>
          ) : (
            messages.map((msg: any) => (
              <div key={msg.id} className="mb-3">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <PersonaAvatar name={msg.sender_id} size={16} showStatus awake={checkAwake(awakeSet, msg.sender_id)} />
                  <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {msg.sender_id}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                    {formatTime(msg.created_at)}
                  </span>
                </div>
                <div className="text-sm pl-5" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
                  <MarkdownRenderer content={msg.content} />
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Compose */}
        <div
          className="shrink-0 px-3 py-2"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message personas..."
              rows={2}
              className="flex-1 px-3 py-2 rounded-lg text-sm outline-none resize-none"
              style={{
                background: 'var(--bg-body)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              }}
            />
            <button
              onClick={handleSend}
              disabled={!draft.trim()}
              className="px-3 py-2 rounded-lg text-xs font-medium disabled:opacity-40"
              style={{
                background: draft.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
                color: draft.trim() ? '#fff' : 'var(--text-muted)',
                border: '1px solid var(--border)',
                cursor: draft.trim() ? 'pointer' : 'default',
              }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
